import React, { useState, useRef, createContext, useContext, useEffect, useCallback } from 'react';
import { db } from '../firebase/config';
import {
  collection, doc, onSnapshot, addDoc, updateDoc, setDoc, getDoc, getDocs, deleteDoc, query, orderBy,
  arrayUnion, arrayRemove, serverTimestamp, where, limit, startAt, endAt
} from 'firebase/firestore';
import { useToast } from './ToastContext';
import logger from '../utils/logger';
import { geocodeCity } from '../utils/geo';
import { fetchTeamLogos } from '../utils/teamLogos';
import { distributeNewPlayer, detectOutlierVotes } from '../utils/calculations';

export const AppContext = createContext();
export const useAppContext = () => useContext(AppContext);

export const AppProvider = ({ userId, children }) => {
  const { showError, showSuccess } = useToast();

  // Buscar escudos dos clubes uma vez por sessão (requer REACT_APP_FOOTBALL_DATA_KEY)
  useEffect(() => { fetchTeamLogos(); }, []);

  // Estado
  const [profile, setProfile] = useState(null);
  const [groups, setGroups] = useState([]);
  const [publicGroups, setPublicGroups] = useState([]);
  const [games, setGames] = useState({});  // { groupId: Game[] }
  const [users, setUsers] = useState({});  // { uid: UserProfile } - cache de perfis
  const [finances, setFinances] = useState({});  // { groupId: Transaction[] }
  const [loading, setLoading] = useState(true);
  const [invites, setInvites] = useState([]);
  const [duels, setDuels] = useState([]);
  const privateProfileRef = useRef(null);
  const usersRef = useRef({});            // espelho de `users` para leitura síncrona
  const pendingProfileFetches = useRef({}); // { uid: Promise } — dedup de fetches em voo
  const groupsLoaded = useRef(false);
  const profileLoaded = useRef(false);

  const trySetReady = useCallback(() => {
    if (profileLoaded.current && groupsLoaded.current) {
      setLoading(false);
    }
  }, []);

  // ─── Perfil do utilizador ───
  useEffect(() => {
    if (!userId) return;

    // Listener do perfil privado (phone, email) — silencioso se não existir ainda
    const unsubPrivate = onSnapshot(
      doc(db, 'users', userId, 'profile', 'private'),
      (snap) => {
        privateProfileRef.current = snap.exists() ? snap.data() : null;
        setProfile(prev => prev ? { ...prev, ...(privateProfileRef.current || {}) } : prev);
      },
      () => {} // não mostrar erro — doc pode ainda não existir
    );

    const unsubPublic = onSnapshot(
      doc(db, 'users', userId, 'profile', 'data'),
      (snap) => {
        if (snap.exists()) {
          const data = snap.data();
          setProfile({ id: userId, ...data, ...(privateProfileRef.current || {}) });
          // Migração silenciosa: geocodificar cidade se lat/lng estiverem ausentes
          if (data.city && !data.lat && !data.lng) {
            geocodeCity(data.city)
              .then(coords => {
                if (coords) updateDoc(snap.ref, { lat: coords.lat, lng: coords.lng })
                  .catch(err => logger.warn('Geocode migration failed:', err));
              })
              .catch(() => {});
          }
        } else {
          setProfile(null);
        }
        profileLoaded.current = true;
        trySetReady();
      },
      (err) => {
        logger.error('Profile listener error:', err);
        showError('Erro ao carregar perfil.');
        profileLoaded.current = true;
        trySetReady();
      }
    );

    // Listener de convites pendentes
    const invitesRef = collection(db, 'users', userId, 'invites');
    const qInvites = query(invitesRef, where('status', '==', 'pending'));
    const unsubInvites = onSnapshot(qInvites, snap => {
      setInvites(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => logger.warn('invites:', err.message));

    return () => { unsubPublic(); unsubPrivate(); unsubInvites(); };
  }, [userId, showError, trySetReady]);

  // ─── Confrontos entre grupos ──────────────────────────────────────────────
  // A coleção é deliberadamente independente de groups/{id}/games: confrontos
  // não devem disparar recorrência, presença, sorteio ou a classificação interna.
  useEffect(() => {
    if (!userId) return undefined;
    const unsub = onSnapshot(collection(db, 'duels'), (snap) => {
      setDuels(snap.docs.map(d => ({ id: d.id, ...d.data() })));
    }, err => logger.warn('duels:', err.message));
    return unsub;
  }, [userId]);

  // ─── Grupos onde o user é membro ───
  useEffect(() => {
    if (!userId) return;
    const unsub = onSnapshot(
      collection(db, 'groups'),
      (snap) => {
        const allGroups = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        const myGroups = allGroups.filter(g =>
          g.ownerId === userId || (g.memberIds && g.memberIds.includes(userId))
        );
        const pubGroups = allGroups.filter(g =>
          g.ownerId !== userId && !(g.memberIds && g.memberIds.includes(userId))
        );
        setGroups(myGroups);
        setPublicGroups(pubGroups);
        groupsLoaded.current = true;
        trySetReady();
      },
      (err) => {
        logger.error('Groups listener error:', err);
        showError('Erro ao carregar grupos.');
        groupsLoaded.current = true;
        trySetReady();
      }
    );
    return unsub;
  }, [userId, showError, trySetReady]);

  // ─── Listener de jogos por grupo ───
  // onlyUpcoming: para grupos públicos (que o user não é membro), só assina
  // jogos scheduled/voting — evita baixar todo o histórico de jogos finalizados
  // (cada um embute objetos completos dos jogadores, com fotos base64).
  const subscribeToGames = useCallback((groupId, onlyUpcoming = false) => {
    const ref = onlyUpcoming
      ? query(collection(db, 'groups', groupId, 'games'), where('status', 'in', ['scheduled', 'voting']))
      : collection(db, 'groups', groupId, 'games');
    const unsub = onSnapshot(
      ref,
      (snap) => {
        const groupGames = snap.docs.map(d => ({ id: d.id, groupId, ...d.data() }));
        setGames(prev => ({ ...prev, [groupId]: groupGames }));
        // Popular cache com guests inline
        groupGames.forEach(game => {
          if (game.guests) {
            setUsers(prev => {
              const updated = { ...prev };
              Object.entries(game.guests).forEach(([guestId, guestData]) => {
                if (!updated[guestId]) {
                  updated[guestId] = { id: guestId, ...guestData };
                }
              });
              return updated;
            });
          }
        });
      }
    );
    return unsub;
  }, []);

  // ─── Listener de finanças por grupo ───
  const subscribeToFinances = useCallback((groupId) => {
    const unsub = onSnapshot(
      query(collection(db, 'groups', groupId, 'finances'), orderBy('createdAt', 'desc')),
      (snap) => {
        const txns = snap.docs.map(d => ({ id: d.id, groupId, ...d.data() }));
        setFinances(prev => ({ ...prev, [groupId]: txns }));
      }
    );
    return unsub;
  }, []);

  // Subscriptions incrementais: apenas adiciona/remove listeners para grupos que mudaram
  const gameSubsRef = useRef({});    // { groupId: unsubscribeFn }
  const financeSubsRef = useRef({}); // { groupId: unsubscribeFn }
  const pubGameSubsRef = useRef({}); // { groupId: unsubscribeFn } — grupos públicos

  useEffect(() => {
    const currentIds = new Set(groups.map(g => g.id));
    // Remover listeners de grupos que saíram
    Object.keys(gameSubsRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        gameSubsRef.current[id]();
        delete gameSubsRef.current[id];
        financeSubsRef.current[id]?.();
        delete financeSubsRef.current[id];
      }
    });
    // Adicionar listeners apenas para grupos novos
    groups.forEach(g => {
      if (!gameSubsRef.current[g.id]) {
        gameSubsRef.current[g.id] = subscribeToGames(g.id);
      }
      if (!financeSubsRef.current[g.id]) {
        financeSubsRef.current[g.id] = subscribeToFinances(g.id);
      }
    });
  }, [groups, subscribeToGames, subscribeToFinances]);

  useEffect(() => {
    const currentIds = new Set(publicGroups.map(g => g.id));
    // Remover listeners de grupos públicos que saíram
    Object.keys(pubGameSubsRef.current).forEach(id => {
      if (!currentIds.has(id)) {
        pubGameSubsRef.current[id]();
        delete pubGameSubsRef.current[id];
      }
    });
    // Adicionar listeners apenas para grupos públicos novos — só jogos futuros
    publicGroups.forEach(g => {
      if (!pubGameSubsRef.current[g.id]) {
        pubGameSubsRef.current[g.id] = subscribeToGames(g.id, true);
      }
    });
  }, [publicGroups, subscribeToGames]);

  // Mantém usersRef em sincronia com o estado para leitura síncrona dentro de
  // callbacks estáveis (sem recriar o callback a cada mudança de `users`).
  useEffect(() => { usersRef.current = users; }, [users]);

  // ─── Cache de perfis de outros users ───
  // Estável (sem deps): evita que os useEffects de páginas que dependem de
  // fetchUserProfile re-executem em cascata a cada perfil carregado.
  // Deduplica fetches concorrentes do mesmo uid (várias páginas pedem ao mesmo tempo).
  const fetchUserProfile = useCallback(async (uid) => {
    if (usersRef.current[uid]) return usersRef.current[uid];
    if (pendingProfileFetches.current[uid]) return pendingProfileFetches.current[uid];
    const p = (async () => {
      try {
        const snap = await getDoc(doc(db, 'users', uid, 'profile', 'data'));
        if (snap.exists()) {
          const userData = { id: uid, ...snap.data() };
          setUsers(prev => ({ ...prev, [uid]: userData }));
          return userData;
        }
      } catch (err) {
        logger.error('Error fetching user profile:', err);
      } finally {
        delete pendingProfileFetches.current[uid];
      }
      return null;
    })();
    pendingProfileFetches.current[uid] = p;
    return p;
  }, []);

  // ─── Validação de telefone único ───
  // phoneIndex/{normalizedPhone} → { uid } — permite detectar duplicatas
  const normalizePhone = (phone) => {
    if (!phone) return null;
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10) return null; // muito curto, ignorar
    // Se já tem o 55, remove antes de normalizar
    const local = digits.startsWith('55') && digits.length > 11 ? digits.slice(2) : digits;
    return '+55' + local;
  };

  const checkPhoneUnique = async (phone, currentUserId) => {
    const normalized = normalizePhone(phone);
    if (!normalized) return; // campo vazio ou inválido — sem validação
    const indexSnap = await getDoc(doc(db, 'phoneIndex', normalized));
    if (indexSnap.exists() && indexSnap.data().uid !== currentUserId) {
      throw new Error('phone_taken');
    }
  };

  const reservePhone = async (newPhone, oldPhone, uid) => {
    const newNorm = normalizePhone(newPhone);
    const oldNorm = normalizePhone(oldPhone);
    // Libera número antigo se mudou
    if (oldNorm && oldNorm !== newNorm) {
      await deleteDoc(doc(db, 'phoneIndex', oldNorm)).catch(() => {});
    }
    // Reserva número novo
    if (newNorm) {
      await setDoc(doc(db, 'phoneIndex', newNorm), { uid });
    }
  };

  // ─── APIs de Perfil ───
  const createProfile = async (profileData) => {
    try {
      const { phone, email, ...publicData } = profileData;
      await checkPhoneUnique(phone, userId);
      // Campos públicos → profile/data
      await setDoc(doc(db, 'users', userId, 'profile', 'data'), {
        ...publicData,
        createdAt: serverTimestamp(),
      });
      // Campos sensíveis → profile/private (apenas o próprio user lê)
      await setDoc(doc(db, 'users', userId, 'profile', 'private'), {
        phone: phone || '',
        email: email || '',
      });
      await reservePhone(phone, null, userId);
    } catch (err) {
      if (err.message === 'phone_taken') {
        showError('Este número de WhatsApp já está cadastrado.');
      } else {
        showError('Erro ao criar perfil.');
      }
      throw err;
    }
  };

  const updateProfile = async (updates) => {
    try {
      const { phone, email, ...publicUpdates } = updates;
      await checkPhoneUnique(phone, userId);
      // Atualiza campos públicos
      if (Object.keys(publicUpdates).length > 0) {
        await updateDoc(doc(db, 'users', userId, 'profile', 'data'), publicUpdates);
      }
      // Atualiza campos privados
      await setDoc(doc(db, 'users', userId, 'profile', 'private'), {
        phone: phone || '',
        email: email || '',
      }, { merge: true });
      await reservePhone(phone, profile?.phone, userId);
    } catch (err) {
      if (err.message === 'phone_taken') {
        showError('Este número de WhatsApp já está cadastrado.');
      } else {
        showError('Erro ao atualizar perfil.');
      }
      throw err;
    }
  };

  // ─── APIs de Grupos ───
  const normalizeGroupSearch = (value = '') => value.normalize('NFD').replace(/[\u0300-\u036f]/g, '').toLowerCase().trim();

  const addGroup = async (groupData) => {
    try {
      const groupRef = await addDoc(collection(db, 'groups'), {
        ...groupData,
        searchName: normalizeGroupSearch(groupData.name),
        searchCity: normalizeGroupSearch(groupData.city),
        ownerId: userId,
        adminIds: [userId],
        memberIds: [userId],
        createdAt: serverTimestamp(),
      });
      return groupRef.id;
    } catch (err) {
      showError('Erro ao criar grupo.');
      throw err;
    }
  };

  // Promove checkins externos para membros normais em jogos ativos do grupo
  // Cobre 3 casos: (1) pendente em externalCheckins, (2) aprovado em
  // confirmedExternalCheckins, (3) já em confirmedIds mas com flag de externo.
  const promoteExternalIfMember = async (groupId, memberId) => {
    try {
      const gamesSnap = await getDocs(collection(db, 'groups', groupId, 'games'));
      for (const gameDoc of gamesSnap.docs) {
        const game = gameDoc.data();
        if (!['scheduled', 'voting'].includes(game.status)) continue;
        const externalCheckins = game.externalCheckins || [];
        const confirmedExt = game.confirmedExternalCheckins || [];
        const inExternal = externalCheckins.includes(memberId);
        const inConfirmedExt = confirmedExt.includes(memberId);
        if (!inExternal && !inConfirmedExt) continue;
        // Se estava aprovado como externo, já garantimos a vaga em confirmedIds.
        // Se estava só pendente, também movemos para confirmedIds (entrar no grupo
        // funciona como aprovação automática).
        await updateDoc(doc(db, 'groups', groupId, 'games', gameDoc.id), {
          externalCheckins: arrayRemove(memberId),
          confirmedExternalCheckins: arrayRemove(memberId),
          confirmedIds: arrayUnion(memberId),
        });
      }
    } catch (err) {
      logger.warn('Erro ao promover checkin externo para membro:', err?.message || err);
    }
  };

  const joinGroup = async (groupId) => {
    try {
      await updateDoc(doc(db, 'groups', groupId), {
        memberIds: arrayUnion(userId),
      });
      await promoteExternalIfMember(groupId, userId);
    } catch (err) {
      showError('Erro ao entrar no grupo.');
      throw err;
    }
  };

  const requestToJoin = async (groupId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const current = groupSnap.data()?.pendingRequests || [];
      if (current.some(r => r.userId === userId)) return; // já solicitou
      await updateDoc(groupRef, {
        pendingRequests: [...current, { userId, requestedAt: new Date().toISOString() }],
      });
    } catch (err) {
      showError('Erro ao solicitar entrada.');
      throw err;
    }
  };

  const approveRequest = async (groupId, requestUserId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const current = groupSnap.data()?.pendingRequests || [];
      await updateDoc(groupRef, {
        pendingRequests: current.filter(r => r.userId !== requestUserId),
        memberIds: arrayUnion(requestUserId),
      });
      await promoteExternalIfMember(groupId, requestUserId);
    } catch (err) {
      showError('Erro ao aprovar solicitacao.');
      throw err;
    }
  };

  const rejectRequest = async (groupId, requestUserId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const current = groupSnap.data()?.pendingRequests || [];
      await updateDoc(groupRef, {
        pendingRequests: current.filter(r => r.userId !== requestUserId),
      });
    } catch (err) {
      showError('Erro ao rejeitar solicitacao.');
      throw err;
    }
  };

  const removeMember = async (groupId, memberId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const data = groupSnap.data() || {};
      const currentMembers = data.memberIds || [];
      const formerMembers = data.formerMembers || [];
      await updateDoc(groupRef, {
        memberIds: currentMembers.filter(id => id !== memberId),
        formerMembers: [...formerMembers, { userId: memberId, removedAt: new Date().toISOString(), removedBy: userId }],
      });
    } catch (err) {
      showError('Erro ao remover membro.');
      throw err;
    }
  };

  const leaveGroup = async (groupId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const data = groupSnap.data() || {};
      const currentMembers = data.memberIds || [];
      const formerMembers = data.formerMembers || [];
      await updateDoc(groupRef, {
        memberIds: currentMembers.filter(id => id !== userId),
        formerMembers: [...formerMembers, { userId, removedAt: new Date().toISOString(), removedBy: userId }],
      });
    } catch (err) {
      showError('Erro ao sair do grupo.');
      throw err;
    }
  };

  const readdMember = async (groupId, memberId) => {
    try {
      const groupRef = doc(db, 'groups', groupId);
      const groupSnap = await getDoc(groupRef);
      const data = groupSnap.data() || {};
      await updateDoc(groupRef, {
        memberIds: [...(data.memberIds || []), memberId],
        formerMembers: (data.formerMembers || []).filter(fm => fm.userId !== memberId),
      });
    } catch (err) {
      showError('Erro ao readicionar membro.');
      throw err;
    }
  };

  const fetchGroup = async (groupId) => {
    try {
      const groupSnap = await getDoc(doc(db, 'groups', groupId));
      if (groupSnap.exists()) {
        return { id: groupSnap.id, ...groupSnap.data() };
      }
      return null;
    } catch (err) {
      showError('Erro ao buscar grupo.');
      return null;
    }
  };

  const fetchGame = async (groupId, gameId) => {
    try {
      const gameSnap = await getDoc(doc(db, 'groups', groupId, 'games', gameId));
      if (gameSnap.exists()) return { id: gameSnap.id, groupId, ...gameSnap.data() };
      return null;
    } catch (err) {
      showError('Erro ao buscar jogo.');
      return null;
    }
  };

  // Limita resultados para seletores (como o desafio de confronto) sem montar
  // uma lista grande de opções na interface. O fallback local cobre grupos legados.
  const searchGroups = useCallback(async (term) => {
    const searchTerm = normalizeGroupSearch(term);
    if (searchTerm.length < 2) return [];
    const found = new Map();
    [...groups, ...publicGroups].filter(group => {
      const name = normalizeGroupSearch(group.name);
      const city = normalizeGroupSearch(group.city);
      return name.includes(searchTerm) || city.includes(searchTerm);
    }).slice(0, 12).forEach(group => found.set(group.id, group));
    try {
      const [byName, byCity] = await Promise.all([
        getDocs(query(collection(db, 'groups'), orderBy('searchName'), startAt(searchTerm), endAt(`${searchTerm}\uf8ff`), limit(12))),
        getDocs(query(collection(db, 'groups'), orderBy('searchCity'), startAt(searchTerm), endAt(`${searchTerm}\uf8ff`), limit(12))),
      ]);
      [...byName.docs, ...byCity.docs].forEach(snapshot => found.set(snapshot.id, { id: snapshot.id, ...snapshot.data() }));
    } catch (err) {
      logger.warn('Busca de grupos:', err?.message || err);
    }
    return Array.from(found.values()).slice(0, 12);
  }, [groups, publicGroups]);

  const updateGroup = async (groupId, updates) => {
    try {
      const searchableUpdates = { ...updates };
      if (updates.name !== undefined) searchableUpdates.searchName = normalizeGroupSearch(updates.name);
      if (updates.city !== undefined) searchableUpdates.searchCity = normalizeGroupSearch(updates.city);
      await updateDoc(doc(db, 'groups', groupId), searchableUpdates);
    } catch (err) {
      showError('Erro ao atualizar grupo.');
      throw err;
    }
  };

  const deleteGroup = async (groupId) => {
    try {
      await deleteDoc(doc(db, 'groups', groupId));
    } catch (err) {
      showError('Erro ao excluir grupo.');
      throw err;
    }
  };

  // ─── APIs de Jogos ───
  const addGame = async (groupId, gameData) => {
    try {
      const gameRef = await addDoc(collection(db, 'groups', groupId, 'games'), {
        ...gameData,
        createdBy: userId,
        status: 'scheduled',
        confirmedIds: [userId],
        teams: null,
        score: { teamA: 0, teamB: 0 },
        votes: {},
        createdAt: serverTimestamp(),
      });
      return gameRef.id;
    } catch (err) {
      showError('Erro ao criar jogo.');
      throw err;
    }
  };

  const addGuest = async (groupId, gameId, guestData) => {
    try {
      const guestId = `guest_${Date.now()}_${Math.random().toString(36).substr(2, 5)}`;
      const guestProfile = {
        name: guestData.name,
        position: guestData.position || 'Meia',
        manualRating: parseFloat(guestData.manualRating) || 3,
        heartTeam: guestData.heartTeam || 'Sem Time',
        dominantFoot: 'Destro',
        photo: '🎽',
        isGuest: true,
      };
      // Guardar guest inline no doc do jogo + adicionar ao confirmedIds
      const gameRef = doc(db, 'groups', groupId, 'games', gameId);
      const gameSnap = await getDoc(gameRef);
      const currentGuests = gameSnap.data()?.guests || {};
      const currentGame = gameSnap.data();
      const updates = {
        guests: { ...currentGuests, [guestId]: guestProfile },
        confirmedIds: arrayUnion(guestId),
      };
      // Se já houve sorteio, adicionar o convidado no time com menos jogadores
      const newTeams = distributeNewPlayer(currentGame, { id: guestId, ...guestProfile });
      if (newTeams) updates.teams = newTeams;
      await updateDoc(gameRef, updates);
      // Popular cache local
      setUsers(prev => ({ ...prev, [guestId]: { id: guestId, ...guestProfile } }));
    } catch (err) {
      showError('Erro ao adicionar convidado.');
      throw err;
    }
  };

  const updateGame = async (groupId, gameId, updates) => {
    try {
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), updates);
    } catch (err) {
      showError('Erro ao atualizar jogo.');
      throw err;
    }
  };

  const setOpenSpots = async (groupId, gameId, count, positions) => {
    try {
      const updates = { openSpots: count };
      if (count === 0) updates.externalCheckins = [];
      if (positions !== undefined) updates.openSpotsPositions = positions;
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), updates);
      // Atualiza flag no grupo para grupos privados serem descobriveis por vagas
      await updateDoc(doc(db, 'groups', groupId), { hasOpenSpots: count > 0 });
    } catch (err) {
      showError('Erro ao definir vagas abertas.');
      throw err;
    }
  };

  const checkinOpenSpot = async (groupId, gameId) => {
    try {
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), {
        externalCheckins: arrayUnion(userId),
      });
    } catch (err) {
      showError('Erro ao fazer check-in.');
      throw err;
    }
  };

  const approveExternalCheckin = async (groupId, gameId, playerId) => {
    try {
      const gameRef = doc(db, 'groups', groupId, 'games', gameId);
      // Se o jogador já é membro do grupo, confirmar como membro normal (não como externo)
      const group = groups.find(g => g.id === groupId);
      const isMember = (group?.memberIds || []).includes(playerId);
      const updates = isMember
        ? { confirmedIds: arrayUnion(playerId), externalCheckins: arrayRemove(playerId) }
        : { confirmedExternalCheckins: arrayUnion(playerId), confirmedIds: arrayUnion(playerId), externalCheckins: arrayRemove(playerId) };
      // Se já houve sorteio, distribuir o jogador aprovado no time com menos jogadores
      const gameSnap = await getDoc(gameRef);
      const currentGame = gameSnap.data();
      if (currentGame?.teams) {
        // Tenta obter perfil do jogador (pode estar em users ou precisa fetch do Firestore)
        let playerObj = users[playerId];
        if (!playerObj) {
          try {
            const profSnap = await getDoc(doc(db, 'users', playerId, 'profile', 'data'));
            if (profSnap.exists()) playerObj = { id: playerId, ...profSnap.data() };
          } catch (e) { /* fallback abaixo */ }
        }
        if (!playerObj) {
          playerObj = { id: playerId, name: 'Jogador', position: 'Meia', manualRating: 3, heartTeam: 'Sem Time', dominantFoot: 'Destro', photo: '👤' };
        }
        const newTeams = distributeNewPlayer(currentGame, { ...playerObj, id: playerId });
        if (newTeams) updates.teams = newTeams;
      }
      await updateDoc(gameRef, updates);
    } catch (err) {
      showError('Erro ao confirmar check-in externo.');
      throw err;
    }
  };

  const rejectExternalCheckin = async (groupId, gameId, playerId) => {
    try {
      const gameRef = doc(db, 'groups', groupId, 'games', gameId);
      await updateDoc(gameRef, {
        externalCheckins: arrayRemove(playerId),
        confirmedExternalCheckins: arrayRemove(playerId),
        confirmedIds: arrayRemove(playerId),
      });
    } catch (err) {
      showError('Erro ao rejeitar check-in externo.');
      throw err;
    }
  };

  // ─── Audit log ────────────────────────────────────────────────────────────
  // Registra ações sensíveis (mudanças de status do jogo, exclusões, etc.)
  // em groups/{groupId}/auditLog. Falhas são silenciosas — auditoria nunca
  // deve impedir uma ação de acontecer.
  const logAuditEvent = async (groupId, action, targetId, details = {}) => {
    if (!groupId || !userId) return;
    try {
      await addDoc(collection(db, 'groups', groupId, 'auditLog'), {
        actorId: userId,
        actorName: profile?.name || null,
        action,
        targetType: 'game',
        targetId: targetId || null,
        details,
        timestamp: serverTimestamp(),
      });
    } catch (err) {
      logger.warn('Falha ao registrar evento de auditoria:', err?.message || err);
    }
  };

  const cancelGame = async (groupId, gameId) => {
    try {
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), {
        status: 'cancelled',
      });
      logAuditEvent(groupId, 'game.cancelled', gameId);
    } catch (err) {
      showError('Erro ao cancelar jogo.');
      throw err;
    }
  };

  const reactivateGame = async (groupId, gameId) => {
    try {
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), {
        status: 'scheduled',
      });
      logAuditEvent(groupId, 'game.reactivated', gameId);
    } catch (err) {
      showError('Erro ao reativar jogo.');
      throw err;
    }
  };

  const deleteGame = async (groupId, gameId) => {
    try {
      // Captura snapshot para o log antes de excluir
      const snap = await getDoc(doc(db, 'groups', groupId, 'games', gameId));
      const prev = snap.exists() ? snap.data() : {};
      await deleteDoc(doc(db, 'groups', groupId, 'games', gameId));
      logAuditEvent(groupId, 'game.deleted', gameId, {
        previousStatus: prev.status || null,
        date: prev.date || null,
        time: prev.time || null,
      });
    } catch (err) {
      showError('Erro ao excluir jogo.');
      throw err;
    }
  };

  /**
   * Exclui um jogo finalizado e remove em cascade:
   *  - entrada no ratingHistory de cada jogador que recebeu nota
   *  - recalcula communityRating de cada jogador afetado
   * Classificação e financeiro são calculados live — atualizados automaticamente.
   */
  const deleteFinishedGame = async (groupId, gameId) => {
    const gameSnap = await getDoc(doc(db, 'groups', groupId, 'games', gameId));
    if (!gameSnap.exists()) return;
    const game = gameSnap.data();

    // Coletamos todos os IDs que podem ter rating deste jogo
    const allIds = new Set([
      ...(game.confirmedIds || []),
      ...(game.teams?.teamA || []).map(p => p.id),
      ...(game.teams?.teamB || []).map(p => p.id),
    ]);

    for (const uid of allIds) {
      if (uid.startsWith('guest_')) continue;
      try {
        const profileRef = doc(db, 'users', uid, 'profile', 'data');
        const snap = await getDoc(profileRef);
        if (!snap.exists()) continue;
        const data = snap.data();
        const history = data.ratingHistory || [];
        const newHistory = history.filter(h => h.gameId !== gameId);
        const hadRatingEntry = newHistory.length < history.length;
        // Decrementar gamesPlayed e descontar gols/assistências
        const currentGamesPlayed = data.gamesPlayed ?? history.length;
        const newGamesPlayed = Math.max(0, currentGamesPlayed - 1);
        const stats = (game.playerStats || {})[uid] || { goals: 0, assists: 0 };
        const profileUpdate = {
          gamesPlayed: newGamesPlayed,
          totalGoals: Math.max(0, (data.totalGoals || 0) - (stats.goals || 0)),
          totalAssists: Math.max(0, (data.totalAssists || 0) - (stats.assists || 0)),
        };
        if (hadRatingEntry) {
          const ratedEntries = newHistory.filter(h => h.rating != null);
          const newRating = ratedEntries.length > 0
            ? Math.round((ratedEntries.reduce((s, h) => s + h.rating, 0) / ratedEntries.length) * 10) / 10
            : null;
          profileUpdate.ratingHistory = newHistory;
          profileUpdate.communityRating = newRating;
        }
        await updateDoc(profileRef, profileUpdate);
      } catch (err) {
        logger.error(`deleteFinishedGame: erro ao atualizar perfil ${uid}:`, err);
      }
    }

    await deleteDoc(doc(db, 'groups', groupId, 'games', gameId));
    logAuditEvent(groupId, 'game.deleted_finished', gameId, {
      date: game.date || null,
      affectedPlayers: allIds.size,
    });
  };

  const summarizePlayerStats = (playerStats = {}) => {
    const entries = Object.entries(playerStats);
    return {
      playersWithStats: entries.length,
      totalGoals: entries.reduce((sum, [, stats]) => sum + (stats?.goals || 0), 0),
      totalAssists: entries.reduce((sum, [, stats]) => sum + (stats?.assists || 0), 0),
    };
  };

  const finalizeGame = async (groupId, gameId) => {
    // Re-ler do Firestore em vez do estado React — evita race condition quando o
    // último votante aciona a finalização automática antes do onSnapshot atualizar.
    const gameSnap = await getDoc(doc(db, 'groups', groupId, 'games', gameId));
    if (!gameSnap.exists()) return;
    const game = { id: gameId, ...gameSnap.data() };

    try {
      await updateDoc(doc(db, 'groups', groupId, 'games', gameId), {
        status: 'finished',
        outlierFilterApplied: true,
      });
      const previousStatus = game.status || null;
      const votesCount = game.votes ? Object.keys(game.votes).length : 0;
      const statsSummary = summarizePlayerStats(game.playerStats || {});
      logAuditEvent(groupId, 'game.finalized', gameId, {
        previousStatus,
        score: game.score || null,
        votesCount,
        ownGoals: game.ownGoals || { teamA: 0, teamB: 0 },
        statsSummary,
        playerStatsSnapshot: game.playerStats || {},
        playersCount: (game.confirmedIds || []).filter(id => !id.startsWith('guest_')).length,
      });

      // Atualizar communityRating de cada jogador votado + gamesPlayed para todos
      // (incluindo externos — eles não recebem votos, mas o jogo conta no perfil deles)
      const confirmedPlayers = (game.confirmedIds || []).filter(pid => !pid.startsWith('guest_'));
      const votesByPlayer = {};
      if (game.votes && Object.keys(game.votes).length > 0) {
        Object.values(game.votes).forEach(voteSheet => {
          Object.entries(voteSheet).forEach(([pid, val]) => {
            if (!votesByPlayer[pid]) votesByPlayer[pid] = [];
            votesByPlayer[pid].push(val);
          });
        });
      }

      const playerStats = game.playerStats || {};
      for (const pid of confirmedPlayers) {
        if (pid.startsWith('guest_')) continue;
        try {
          const profileRef = doc(db, 'users', pid, 'profile', 'data');
          const profileSnap = await getDoc(profileRef);
          if (!profileSnap.exists()) continue;
          const data = profileSnap.data();
          // Sempre incrementar gamesPlayed + acumular gols/assistências
          const currentGamesPlayed = data.gamesPlayed ?? (data.ratingHistory?.length || 0);
          const stats = playerStats[pid] || { goals: 0, assists: 0 };
          const profileUpdate = {
            gamesPlayed: currentGamesPlayed + 1,
            totalGoals: (data.totalGoals || 0) + (stats.goals || 0),
            totalAssists: (data.totalAssists || 0) + (stats.assists || 0),
          };
          // Se o jogador foi votado, atualizar também ratingHistory e communityRating
          if (votesByPlayer[pid]) {
            const rawVotes = votesByPlayer[pid];
            const { validVotes, outlierVotes } = detectOutlierVotes(rawVotes);
            const filteredAvg = validVotes.reduce((a, b) => a + b, 0) / validVotes.length;
            const voteDistribution = {};
            validVotes.forEach(v => {
              const key = String(v);
              voteDistribution[key] = (voteDistribution[key] || 0) + 1;
            });
            const history = data.ratingHistory || [];
            history.push({
              gameId,
              groupId,
              date: game.date,
              rating: Math.round(filteredAvg * 10) / 10,
              voteDistribution,
              outlierVotes,
            });
            // Manter últimos 50 jogos
            if (history.length > 50) history.splice(0, history.length - 50);
            const ratedEntries = history.filter(h => h.rating != null);
            const newCommunityRating = ratedEntries.length > 0
              ? Math.round((ratedEntries.reduce((s, h) => s + h.rating, 0) / ratedEntries.length) * 10) / 10
              : null;
            profileUpdate.ratingHistory = history;
            profileUpdate.communityRating = newCommunityRating;
          }
          await updateDoc(profileRef, profileUpdate);
        } catch (e) {
          // Silently skip if permission denied for specific user
          logger.warn('Não foi possível atualizar perfil de', pid, e.message);
        }
      }

      // Criar próximo jogo automaticamente se o grupo for recorrente
      const group = groups.find(g => g.id === groupId);
      if (group?.isRecurring !== false) {
        // Calcula a próxima data com base no dayOfWeek do grupo a partir de hoje,
        // para evitar que jogos retroativos (data editada) gerem próximo jogo no passado.
        let nextDateStr;
        const dayMap = { dom: 0, seg: 1, ter: 2, qua: 3, qui: 4, sex: 5, sab: 6 };
        const targetDay = group.dayOfWeek ? dayMap[group.dayOfWeek] : null;
        if (targetDay !== null && targetDay !== undefined) {
          const today = new Date(); today.setHours(0, 0, 0, 0);
          const daysUntilNext = ((targetDay - today.getDay() + 7) % 7) || 7;
          const nextDate = new Date(today);
          nextDate.setDate(today.getDate() + daysUntilNext);
          nextDateStr = nextDate.toISOString().split('T')[0];
        } else {
          // Fallback: +7 dias a partir da data do jogo
          const nextDate = new Date(game.date);
          nextDate.setDate(nextDate.getDate() + 7);
          nextDateStr = nextDate.toISOString().split('T')[0];
        }
        // Guardrail: só cria se ainda não existe um jogo para a mesma data + hora
        try {
          const existing = await getDocs(query(
            collection(db, 'groups', groupId, 'games'),
            where('date', '==', nextDateStr),
            where('time', '==', game.time)
          ));
          if (!existing.empty) {
            logger.log('Jogo recorrente já existe para', nextDateStr, game.time, '— skip.');
          } else {
            await addDoc(collection(db, 'groups', groupId, 'games'), {
              date: nextDateStr,
              time: game.time,
              maxPlayers: game.maxPlayers,
              playersPerTeam: game.playersPerTeam,
              pricePerPerson: game.pricePerPerson,
              createdBy: userId,
              status: 'scheduled',
              confirmedIds: [],
              teams: null,
              score: { teamA: 0, teamB: 0 },
              votes: {},
              createdAt: serverTimestamp(),
            });
          }
        } catch (e) {
          logger.warn('Falha ao criar próximo jogo:', e?.message || e);
        }
      }
    } catch (err) {
      showError('Erro ao finalizar jogo.');
      throw err;
    }
  };

  // ─── APIs de Confrontos ───────────────────────────────────────────────────
  const isGroupAdmin = (group) => group && (group.ownerId === userId || (group.adminIds || []).includes(userId));

  const createDuel = async ({ creatorGroupId, opponentGroupId, date, time, courtName, courtAddress, sport, votingEnabled, playersPerTeam }) => {
    const creatorGroup = groups.find(g => g.id === creatorGroupId);
    const opponentGroup = [...groups, ...publicGroups].find(g => g.id === opponentGroupId);
    if (!isGroupAdmin(creatorGroup)) throw new Error('not_group_admin');
    if (!opponentGroup || creatorGroupId === opponentGroupId) throw new Error('invalid_opponent');
    try {
      const ref = await addDoc(collection(db, 'duels'), {
        creatorId: userId,
        creatorGroupId,
        opponentGroupId,
        creatorGroup: { id: creatorGroup.id, name: creatorGroup.name, sport: creatorGroup.sport || sport || 'Futebol' },
        opponentGroup: { id: opponentGroup.id, name: opponentGroup.name, sport: opponentGroup.sport || sport || 'Futebol' },
        date,
        time,
        courtName: courtName || '',
        courtAddress: courtAddress || '',
        sport: sport || creatorGroup.sport || 'Futebol',
        playersPerTeam: Math.min(11, Math.max(3, parseInt(playersPerTeam, 10) || 6)),
        status: 'pending',
        creatorRoster: [],
        opponentRoster: [],
        score: { creator: 0, opponent: 0 },
        playerStats: {},
        voting: {
          [creatorGroupId]: { enabled: votingEnabled !== false, status: 'pending', votes: {} },
          [opponentGroupId]: { enabled: true, status: 'pending', votes: {} },
        },
        createdAt: serverTimestamp(),
      });
      return ref.id;
    } catch (err) {
      showError('Erro ao criar confronto.');
      throw err;
    }
  };

  const respondToDuel = async (duelId, accepted, opponentVotingEnabled = true) => {
    const duel = duels.find(d => d.id === duelId);
    const opponentGroup = groups.find(g => g.id === duel?.opponentGroupId);
    if (!duel || !isGroupAdmin(opponentGroup)) throw new Error('not_group_admin');
    try {
      const voting = { ...(duel.voting || {}) };
      voting[duel.opponentGroupId] = { ...(voting[duel.opponentGroupId] || {}), enabled: opponentVotingEnabled, status: 'pending', votes: {} };
      await updateDoc(doc(db, 'duels', duelId), {
        status: accepted ? 'accepted' : 'declined',
        respondedBy: userId,
        respondedAt: serverTimestamp(),
        voting,
      });
    } catch (err) {
      showError('Erro ao responder ao desafio.');
      throw err;
    }
  };

  const updateDuelRoster = async (duelId, groupId, roster) => {
    const duel = duels.find(d => d.id === duelId);
    const group = groups.find(g => g.id === groupId);
    if (!duel || !isGroupAdmin(group) || !['accepted', 'pending'].includes(duel.status)) throw new Error('not_allowed');
    const field = duel.creatorGroupId === groupId ? 'creatorRoster' : duel.opponentGroupId === groupId ? 'opponentRoster' : null;
    if (!field) throw new Error('not_participant');
    const oppositeField = field === 'creatorRoster' ? 'opponentRoster' : 'creatorRoster';
    const oppositeIds = new Set((duel[oppositeField] || []).filter(player => !player.isGuest).map(player => player.id));
    if (roster.some(player => !player.isGuest && oppositeIds.has(player.id))) throw new Error('duplicate_player');
    try {
      await updateDoc(doc(db, 'duels', duelId), { [field]: roster });
    } catch (err) {
      showError('Erro ao atualizar a escalação.');
      throw err;
    }
  };

  const addDuelGuest = async (duelId, groupId, guestData) => {
    const duel = duels.find(d => d.id === duelId);
    const group = groups.find(g => g.id === groupId);
    if (!duel || !isGroupAdmin(group)) throw new Error('not_group_admin');
    const field = duel.creatorGroupId === groupId ? 'creatorRoster' : duel.opponentGroupId === groupId ? 'opponentRoster' : null;
    if (!field) throw new Error('not_participant');
    const guest = {
      id: `duel_guest_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
      name: guestData.name,
      position: guestData.position || 'Meia',
      manualRating: parseFloat(guestData.manualRating) || 5,
      heartTeam: guestData.heartTeam || 'Sem Time',
      dominantFoot: 'Destro',
      photo: '🎽',
      isGuest: true,
    };
    await updateDuelRoster(duelId, groupId, [...(duel[field] || []), guest]);
  };

  const setDuelVoting = async (duelId, groupId, enabled) => {
    const duel = duels.find(d => d.id === duelId);
    const group = groups.find(g => g.id === groupId);
    if (!duel || !isGroupAdmin(group) || duel.status === 'finished') throw new Error('not_allowed');
    const voting = { ...(duel.voting || {}) };
    voting[groupId] = { ...(voting[groupId] || {}), enabled, status: 'pending', votes: {} };
    await updateDoc(doc(db, 'duels', duelId), { voting });
  };

  const finishDuel = async (duelId, score, playerStats) => {
    const duel = duels.find(d => d.id === duelId);
    if (!duel || duel.creatorId !== userId || duel.status !== 'accepted') throw new Error('not_allowed');
    try {
      await updateDoc(doc(db, 'duels', duelId), {
        status: 'finished', score, playerStats: playerStats || {}, finishedAt: serverTimestamp(), finishedBy: userId,
      });
      const players = [...(duel.creatorRoster || []), ...(duel.opponentRoster || [])];
      for (const player of players) {
        if (player.isGuest || String(player.id).startsWith('duel_guest_')) continue;
        try {
          const profileRef = doc(db, 'users', player.id, 'profile', 'data');
          const snap = await getDoc(profileRef);
          if (!snap.exists()) continue;
          const data = snap.data();
          const stats = (playerStats || {})[player.id] || {};
          const history = data.ratingHistory || [];
          if (!history.some(item => item.duelId === duelId)) {
            history.push({ duelId, type: 'duel', date: duel.date, rating: null });
          }
          await updateDoc(profileRef, {
            gamesPlayed: (data.gamesPlayed || 0) + 1,
            totalGoals: (data.totalGoals || 0) + (stats.goals || 0),
            totalAssists: (data.totalAssists || 0) + (stats.assists || 0),
            ratingHistory: history.slice(-50),
          });
        } catch (err) { logger.warn('Não foi possível atualizar histórico de confronto:', err?.message || err); }
      }
    } catch (err) {
      showError('Erro ao finalizar confronto.');
      throw err;
    }
  };

  const deleteDuel = async (duelId) => {
    const duel = duels.find(item => item.id === duelId);
    if (!duel || duel.creatorId !== userId) throw new Error('not_allowed');
    try {
      if (duel.status === 'finished') {
        const players = new Map();
        [...(duel.creatorRoster || []), ...(duel.opponentRoster || [])].filter(player => !player.isGuest && !String(player.id).startsWith('duel_guest_')).forEach(player => players.set(player.id, player));
        for (const playerId of players.keys()) {
          try {
            const profileRef = doc(db, 'users', playerId, 'profile', 'data');
            const snapshot = await getDoc(profileRef);
            if (!snapshot.exists()) continue;
            const data = snapshot.data();
            const history = (data.ratingHistory || []).filter(item => item.duelId !== duelId);
            const rated = history.filter(item => item.rating != null);
            const stats = (duel.playerStats || {})[playerId] || {};
            await updateDoc(profileRef, {
              gamesPlayed: Math.max(0, (data.gamesPlayed || 0) - 1),
              totalGoals: Math.max(0, (data.totalGoals || 0) - (stats.goals || 0)),
              totalAssists: Math.max(0, (data.totalAssists || 0) - (stats.assists || 0)),
              ratingHistory: history,
              communityRating: rated.length > 0 ? Math.round((rated.reduce((sum, item) => sum + item.rating, 0) / rated.length) * 10) / 10 : null,
            });
          } catch (err) { logger.warn('Não foi possível reverter histórico de confronto:', err?.message || err); }
        }
      }
      await deleteDoc(doc(db, 'duels', duelId));
    } catch (err) {
      showError('Erro ao excluir confronto.');
      throw err;
    }
  };

  const submitDuelVotes = async (duelId, groupId, votes) => {
    const duel = duels.find(d => d.id === duelId);
    const group = groups.find(g => g.id === groupId);
    const isMember = group && (group.memberIds || []).includes(userId);
    if (!duel || !isMember || duel.status !== 'finished') throw new Error('not_allowed');
    const side = duel.creatorGroupId === groupId ? duel.creatorRoster : duel.opponentRoster;
    const eligible = new Set((side || []).filter(p => !p.isGuest).map(p => p.id));
    if (!eligible.has(userId)) throw new Error('not_participant');
    const cleanVotes = Object.fromEntries(Object.entries(votes).filter(([id]) => id !== userId && eligible.has(id)));
    const voting = { ...(duel.voting || {}) };
    voting[groupId] = { ...(voting[groupId] || {}), votes: { ...(voting[groupId]?.votes || {}), [userId]: cleanVotes } };
    await updateDoc(doc(db, 'duels', duelId), { voting });
    const expectedVoters = (side || []).filter(player => !player.isGuest).map(player => player.id);
    const votedIds = Object.keys(voting[groupId].votes || {});
    if (expectedVoters.length > 0 && expectedVoters.every(playerId => votedIds.includes(playerId))) {
      await finalizeDuelVoting(duelId, groupId);
    }
  };

  const finalizeDuelVoting = async (duelId, groupId) => {
    const duel = duels.find(d => d.id === duelId);
    const group = groups.find(g => g.id === groupId);
    const isMember = group && (group.memberIds || []).includes(userId);
    if (!duel || !isMember || duel.status !== 'finished') throw new Error('not_allowed');
    const voting = duel.voting?.[groupId] || {};
    if (!voting.enabled || voting.status === 'finished') return;
    const side = duel.creatorGroupId === groupId ? duel.creatorRoster : duel.opponentRoster;
    const eligible = new Set((side || []).filter(player => !player.isGuest).map(player => player.id));
    const votesByPlayer = {};
    Object.values(voting.votes || {}).forEach(sheet => Object.entries(sheet).filter(([pid]) => eligible.has(pid)).forEach(([pid, value]) => {
      if (!votesByPlayer[pid]) votesByPlayer[pid] = [];
      votesByPlayer[pid].push(value);
    }));
    for (const [pid, rawVotes] of Object.entries(votesByPlayer)) {
      try {
        const profileRef = doc(db, 'users', pid, 'profile', 'data');
        const snap = await getDoc(profileRef);
        if (!snap.exists()) continue;
        const data = snap.data();
        const rating = Math.round((rawVotes.reduce((sum, value) => sum + value, 0) / rawVotes.length) * 10) / 10;
        const previousHistory = data.ratingHistory || [];
        const hasDuelEntry = previousHistory.some(item => item.duelId === duelId);
        const history = (hasDuelEntry
          ? previousHistory.map(item => item.duelId === duelId ? { ...item, rating } : item)
          : [...previousHistory, { duelId, type: 'duel', date: duel.date, rating }]
        ).slice(-50);
        const rated = history.filter(h => h.rating != null);
        await updateDoc(profileRef, {
          ratingHistory: history,
          communityRating: Math.round((rated.reduce((sum, item) => sum + item.rating, 0) / rated.length) * 10) / 10,
        });
      } catch (err) { logger.warn('Não foi possível atualizar votação de confronto:', err?.message || err); }
    }
    await updateDoc(doc(db, 'duels', duelId), { [`voting.${groupId}.status`]: 'finished' });
  };

  // ─── APIs de Finanças ───
  const addFinance = async (groupId, data) => {
    try {
      await addDoc(collection(db, 'groups', groupId, 'finances'), {
        ...data,
        createdBy: userId,
        createdAt: serverTimestamp(),
      });
    } catch (err) {
      showError('Erro ao registrar transação.');
      throw err;
    }
  };

  const deleteFinance = async (groupId, financeId) => {
    try {
      await deleteDoc(doc(db, 'groups', groupId, 'finances', financeId));
    } catch (err) {
      showError('Erro ao remover transação.');
      throw err;
    }
  };

  // ─── APIs de Convites ───
  const respondInvite = async (inviteId, accept) => {
    try {
      const inviteRef = doc(db, 'users', userId, 'invites', inviteId);
      const inviteSnap = await getDoc(inviteRef);
      if (!inviteSnap.exists()) return;

      const invite = inviteSnap.data();
      const nextStatus = accept ? 'accepted' : 'declined';

      if (!accept) {
        await updateDoc(inviteRef, {
          status: nextStatus,
        });

        if (invite.scope === 'group' && invite.groupId) {
          await setDoc(doc(db, 'groups', invite.groupId, 'playerInvites', userId), {
            status: nextStatus,
            respondedAt: serverTimestamp(),
          }, { merge: true });
        }

        if (invite.scope === 'game' && invite.groupId && invite.gameId) {
          await setDoc(doc(db, 'groups', invite.groupId, 'games', invite.gameId, 'playerInvites', userId), {
            status: nextStatus,
            respondedAt: serverTimestamp(),
          }, { merge: true });
        }

        showSuccess('Convite recusado.');
        return;
      }

      if (invite.scope === 'game' && invite.groupId && invite.gameId) {
        const gameRef = doc(db, 'groups', invite.groupId, 'games', invite.gameId);
        const gameSnap = await getDoc(gameRef);
        if (!gameSnap.exists()) {
          showError('Esse jogo não está mais disponível.');
          return;
        }

        const gameData = gameSnap.data();
        if (gameData.status === 'cancelled' || gameData.status === 'finished') {
          showError('Esse jogo não aceita mais confirmações.');
          return;
        }

        await updateDoc(gameRef, {
          externalCheckins: arrayUnion(userId),
        });
        await updateDoc(inviteRef, {
          status: nextStatus,
        });
        await deleteDoc(doc(db, 'groups', invite.groupId, 'games', invite.gameId, 'playerInvites', userId));
        showSuccess('Check-in enviado para o próximo jogo.');
        return;
      }

      if (invite.groupId) {
        await updateDoc(doc(db, 'groups', invite.groupId), {
          memberIds: arrayUnion(userId),
        });
        await updateDoc(inviteRef, {
          status: nextStatus,
        });
        await deleteDoc(doc(db, 'groups', invite.groupId, 'playerInvites', userId));
        showSuccess('Você entrou no grupo.');
      }
    } catch (err) {
      logger.error('Erro ao responder convite:', err);
      showError('Erro ao responder convite.');
    }
  };

  // Helper: todos os jogos flat
  const allGames = Object.values(games).flat();

  // Helper: encontrar jogo por ID
  const findGame = (gameId) => allGames.find(g => g.id === gameId);

  return (
    <AppContext.Provider value={{
      userId,
      profile,
      groups,
      publicGroups,
      games,
      allGames,
      duels,
      finances,
      users,
      loading,
      setLoading,
      findGame,
      fetchUserProfile,
      createProfile,
      updateProfile,
      addGroup,
      joinGroup,
      requestToJoin,
      approveRequest,
      rejectRequest,
      removeMember,
      leaveGroup,
      readdMember,
      fetchGroup,
      fetchGame,
      searchGroups,
      updateGroup,
      deleteGroup,
      addGame,
      addGuest,
      logAuditEvent,
      setOpenSpots,
      checkinOpenSpot,
      approveExternalCheckin,
      rejectExternalCheckin,
      updateGame,
      cancelGame,
      reactivateGame,
      deleteGame,
      deleteFinishedGame,
      finalizeGame,
      createDuel,
      respondToDuel,
      updateDuelRoster,
      addDuelGuest,
      setDuelVoting,
      finishDuel,
      deleteDuel,
      submitDuelVotes,
      finalizeDuelVoting,
      addFinance,
      deleteFinance,
      invites,
      respondInvite,
    }}>
      {children}
    </AppContext.Provider>
  );
};

